import { Component, OnDestroy, OnInit } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { KeycloakService } from 'keycloak-angular';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UserService } from './services/user/user.service';
import { VlIconLibraryService } from 'vl-tailwind-layout';
import { SocketIOService, SocketUserSessionDeletedNotification } from "./services/socketio/socketio.service";
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from './services/auth.service';
import { Country, Timezone, User } from './models/verylys';
import AOS from 'aos';
import { KeycloakInitializerService } from './services/keycloakInitializer.service';
import { formalizedItemFromUrl } from './utilities/window';
import { GtagService } from './services/gtag/gtag.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
    private destroy$: Subject<void> = new Subject();

    constructor(private meta: Meta,
        private userService: UserService,
        private iconLibraryService: VlIconLibraryService,
        private socketService: SocketIOService,
        private authService: AuthService,
        private router: Router,
        private keycloakInitializer: KeycloakInitializerService,
        private gTagService: GtagService,
        private titleService: Title, private metaService: Meta) {
        this.titleService.setTitle($localize`:@@seo.title:VeryGames - Game Server Provider`);
        this.metaService.updateTag({ name: 'description', content: $localize`:@@seo.description:High Performance Game Servers hosting: FS22, FS25, Minecraft, CSGO, ARK, TeamSpeak... 480Gbps DDOS protection included with all services!` });
    }

    ngOnInit() {
        this.addGtagScript();

        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                let url = window.location.href;
                url = url.split('?')[0];
                url = url.split('#')[0];

                let link: HTMLLinkElement = document.querySelector('link[rel="canonical"]') || null;

                if (link) {
                    link.href = url;
                } else {
                    link = document.createElement('link');
                    link.setAttribute('rel', 'canonical');
                    link.setAttribute('href', url);
                    document.head.appendChild(link);
                }
            });

        //this.keycloakInitializer.initialize();
        this.keycloakInitializer.initialized
            .pipe(takeUntil(this.destroy$))
            .subscribe(async () => {
                const isLoggenIn: boolean = await this.authService.isLoggedIn()

                if (!isLoggenIn) {
                    this.userService.leaveAllRoom();
                } else {
                    this.loggedProcess();
                }
            });

        document.body.classList.remove('overflow-hidden'); // Offcanvas security

        AOS.init({
            once: true,
            anchorPlacement: "top-bottom",
            debounceDelay: 50,
            throttleDelay: 50,
            offset: 100,
            duration: 400
        });
        AOS.refresh();

        this.router.events
            .pipe(
                takeUntil(this.destroy$),
                filter(event => event instanceof NavigationEnd)
            ).subscribe(() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' }));

        const currencyId: string = this.userService.getCurrencyFromLocalStorage();
        // calling the setLocalStorageCurrency after getting it prevent to have a null currency :), check on the setLocalStorageCurrency method
        this.userService.setLocalStorageCurrency(currencyId, true);

        const countryId: string = this.userService.getCountryFromLocalStorage();
        this.userService.setLocalStorageCountry(countryId, true);

        const locale: string = this.userService.getLocaleFromLocalStorage();
        this.userService.setLocalStorageLocale(locale);

        this.meta.addTag({ name: "version", content: environment.build_version });

        this.iconLibraryService.registerPack('font-awesome-solid', { packClass: 'fas', iconPrefix: 'fa' });
        this.iconLibraryService.registerPack('font-awesome-brands', { packClass: 'fab', iconPrefix: 'fa' });
        this.iconLibraryService.registerPack('font-awesome-regular', { packClass: 'far', iconPrefix: 'fa' });

        this.gTagService.trackHome(locale);
    }

    private addGtagScript() {
        if (!environment.googleTrackingId) {
            return;
        }

        const gtagScript = document.createElement('script');
        gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${environment.googleTrackingId}`;
        gtagScript.async = true;
        document.head.appendChild(gtagScript);

        // Configuration du script gtag
        const gtagConfig = document.createElement('script');
        gtagConfig.innerHTML = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${environment.googleTrackingId}');
        `;

        document.head.appendChild(gtagConfig);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewInit() {

    }

    private async loggedProcess(): Promise<void> {
        this.socketService.connect(); // Connect socket because of bearer tokens, if needed, trying to use .auth() method
        const me = await this.userService.me({ populate: { path: "timezone country" } }).toPromise();

        if (!me) {
            this.authService.name = null;
            this.authService.id = null;
            return;
        }

        this.authService.name = `${me.keycloakDatas?.firstName} ${me.keycloakDatas.lastName}`;

        this.userService.setLocalStorageCurrency(me.currency?.toString(), true);
        this.userService.setLocalStorageCountry((me.country as Country)._id, true);
        this.userService.setLocalStorageLocale((me.country as Country).parameters?.country_code?.value);
        this.userService.setLocalStorageTimezone((me.timezone as Timezone)?.parameters?.tz_code?.value ?? 'Europe/Paris');

        this.userService.joinSocketRoom(me._id);

        this.userService.onSessionDeleted
            .pipe(takeUntil(this.destroy$))
            .subscribe((message: SocketUserSessionDeletedNotification) => {
                if (message.resourceId == me._id) {
                    this.authService.logOut();
                }
            });
    }
}
